/**
 * Shared tokens + utilities for the EnglishTest.UK auth preview screens.
 */
import React, { useRef } from "react";

export const T = {
  bg: "#FCFAF3",
  bg2: "#F5F1E0",
  cardBg: "#FFFFFF",
  cardBgWarm: "#FBF6E6",
  border: "#E5DCC0",
  borderSoft: "#EEE6CE",
  input: "#FFFFFF",
  primary: "#F77F00",
  primaryDark: "#D26900",
  primaryGlow: "rgba(247,127,0,0.45)",
  primaryGlowSoft: "rgba(247,127,0,0.22)",
  secondary: "#D62828",
  text: "#003049",
  textMuted: "rgba(0,48,73,0.65)",
  textOnPrimary: "#FFFFFF",
  cream: "#EAE2B7",
  success: "#0C4B66",
};

export function useClickSound() {
  const ctxRef = useRef(null);
  const getCtx = () => {
    if (!ctxRef.current) {
      try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); }
      catch (e) { return null; }
    }
    return ctxRef.current;
  };
  return (variant = "primary") => {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(4000, now);
    if (variant === "primary") {
      osc.type = "square";
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(1320, now + 0.06);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    } else {
      osc.type = "sine";
      osc.frequency.setValueAtTime(1200, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.04, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    }
    osc.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.3);
  };
}

export const PrimaryBtn = ({ children, type = "button", className = "", onClick, ...rest }) => {
  const play = useClickSound();
  return (
    <button
      type={type}
      onClick={(e) => { play("primary"); onClick && onClick(e); }}
      className={`relative inline-flex items-center justify-center gap-2 h-14 px-8 font-bold uppercase tracking-[0.22em] rounded-sm transition-all duration-300 active:scale-[0.97] ${className}`}
      style={{
        background: T.primary,
        color: T.textOnPrimary,
        border: `2px solid ${T.primary}`,
        boxShadow: `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)`,
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = T.primaryDark;
        e.currentTarget.style.borderColor = T.primaryDark;
        e.currentTarget.style.boxShadow = `0 0 34px ${T.primaryGlow}, inset 0 0 14px rgba(255,255,255,0.28)`;
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = T.primary;
        e.currentTarget.style.borderColor = T.primary;
        e.currentTarget.style.boxShadow = `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)`;
        e.currentTarget.style.transform = "";
      }}
      {...rest}
    >
      {children}
    </button>
  );
};

export const NeonInput = ({ label, icon: Icon, type = "text", className = "", ...rest }) => (
  <label className="block">
    <div className="mono text-[10px] uppercase tracking-[0.3em] mb-2 font-bold" style={{ color: T.primary }}>{label}</div>
    <div className="relative">
      {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: T.textMuted }} />}
      <input
        type={type}
        className={`w-full h-14 rounded-sm font-mono px-4 ${Icon ? "pl-12" : ""} outline-none transition-all text-base ${className}`}
        style={{
          background: T.input,
          border: `2px solid ${T.border}`,
          color: T.text,
        }}
        onFocus={(e) => { e.target.style.borderColor = T.primary; e.target.style.boxShadow = `0 0 18px ${T.primaryGlow}`; }}
        onBlur={(e) => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; }}
        {...rest}
      />
    </div>
  </label>
);

export const HudFrame = ({ children, className = "", style = {}, color = T.primary }) => (
  <div className={`relative ${className}`} style={style}>
    <span className="absolute -top-1 -left-1 w-3.5 h-3.5 border-t-2 border-l-2" style={{ borderColor: color }} />
    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 border-t-2 border-r-2" style={{ borderColor: color }} />
    <span className="absolute -bottom-1 -left-1 w-3.5 h-3.5 border-b-2 border-l-2" style={{ borderColor: color }} />
    <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 border-b-2 border-r-2" style={{ borderColor: color }} />
    {children}
  </div>
);

export const AIPropsBackground = () => (
  <>
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
      {/* big radial glow */}
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full"
        style={{ background: `radial-gradient(circle, ${T.primaryGlow} 0%, transparent 60%)`, filter: "blur(40px)" }} />
      <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full"
        style={{ background: `radial-gradient(circle, rgba(0,48,73,0.10) 0%, transparent 60%)`, filter: "blur(40px)" }} />

      {/* grid */}
      <div className="absolute inset-0" style={{
        backgroundImage: `linear-gradient(${T.text} 1px, transparent 1px), linear-gradient(90deg, ${T.text} 1px, transparent 1px)`,
        backgroundSize: "64px 64px",
        opacity: 0.05,
      }} />

      {/* Holographic globe */}
      <div className="absolute top-16 right-16 w-32 h-32 ai-float opacity-80">
        <svg viewBox="0 0 120 120" className="w-full h-full ai-spin-slow" fill="none">
          <circle cx="60" cy="60" r="50" stroke={T.primary} strokeWidth="1" opacity="0.6" />
          <ellipse cx="60" cy="60" rx="50" ry="20" stroke={T.primary} strokeWidth="1" opacity="0.4" />
          <ellipse cx="60" cy="60" rx="50" ry="35" stroke={T.primary} strokeWidth="1" opacity="0.4" />
          <ellipse cx="60" cy="60" rx="20" ry="50" stroke={T.primary} strokeWidth="1" opacity="0.4" />
          <ellipse cx="60" cy="60" rx="35" ry="50" stroke={T.primary} strokeWidth="1" opacity="0.4" />
          <line x1="10" y1="60" x2="110" y2="60" stroke={T.primary} strokeWidth="1.2" />
          <line x1="60" y1="10" x2="60" y2="110" stroke={T.primary} strokeWidth="1.2" />
          <circle cx="40" cy="42" r="2.5" fill={T.primary} />
          <circle cx="78" cy="50" r="2.5" fill={T.primary} />
          <circle cx="55" cy="80" r="2.5" fill={T.primary} />
        </svg>
      </div>

      {/* Floating English letters — animated, scattered */}
      {[
        { ch: "A", x: "8%",  y: "18%", size: 64, rotate: -8,  delay: "0s",   dur: "5s" },
        { ch: "B", x: "92%", y: "12%", size: 48, rotate: 12,  delay: "0.7s", dur: "6s" },
        { ch: "C", x: "14%", y: "62%", size: 56, rotate: -4,  delay: "1.2s", dur: "5.5s" },
        { ch: "a", x: "78%", y: "72%", size: 42, rotate: 6,   delay: "0.4s", dur: "6.5s" },
        { ch: "&", x: "50%", y: "8%",  size: 38, rotate: -12, delay: "1.5s", dur: "5s" },
        { ch: "Æ", x: "88%", y: "45%", size: 36, rotate: 8,   delay: "2s",   dur: "6s" },
        { ch: "?", x: "6%",  y: "90%", size: 42, rotate: -6,  delay: "1.8s", dur: "5.2s" },
      ].map((l, i) => (
        <div key={i} className="absolute ai-float-slow font-bold select-none pointer-events-none"
          style={{
            left: l.x, top: l.y,
            fontSize: `${l.size}px`,
            color: T.primary,
            opacity: 0.18,
            transform: `rotate(${l.rotate}deg)`,
            fontFamily: "'Rajdhani', sans-serif",
            animationDelay: l.delay,
            animationDuration: l.dur,
            textShadow: `0 0 14px ${T.primaryGlow}`,
          }}
        >
          {l.ch}
        </div>
      ))}

      {/* Vertical word strip — typewriter-style English words */}
      <div className="absolute top-1/3 left-6 mono text-[11px] tracking-[0.2em] opacity-30 ai-float-slow leading-relaxed" style={{ color: T.text, animationDelay: "0.5s" }}>
        <div>SUBJECT</div>
        <div>PREDICATE</div>
        <div>CLAUSE</div>
        <div>SYNTAX</div>
        <div>LEXIS</div>
      </div>

      {/* Waveform */}
      <div className="absolute top-1/2 right-8 -translate-y-1/2 flex items-end gap-1 h-12 opacity-60 ai-float-slow">
        {[0.3, 0.6, 0.2, 0.8, 0.4, 1.0, 0.3, 0.7, 0.4, 0.5].map((h, i) => (
          <span key={i} className="w-1 rounded-sm ai-wave"
            style={{
              height: `${h * 100}%`,
              background: T.primary,
              animationDelay: `${i * 0.09}s`,
              boxShadow: `0 0 4px ${T.primaryGlow}`,
            }} />
        ))}
      </div>

      {/* Binary stream */}
      <div className="absolute bottom-16 left-16 mono text-[10px] leading-tight tracking-wider opacity-30 ai-float-slow" style={{ color: T.primary }}>
        {["10110101", "01001011", "11010010", "00101110"].map((b, i) => (
          <div key={i} style={{ opacity: 1 - i * 0.2 }}>{b}</div>
        ))}
      </div>

      {/* Orbital ring */}
      <div className="absolute bottom-24 right-24 w-20 h-20 opacity-60">
        <svg viewBox="0 0 80 80" className="w-full h-full ai-spin-slow" fill="none">
          <circle cx="40" cy="40" r="34" stroke={T.primary} strokeWidth="1" strokeDasharray="2 4" opacity="0.6" />
          <circle cx="40" cy="6" r="3" fill={T.primary} />
          <circle cx="74" cy="40" r="2" fill={T.primary} opacity="0.6" />
        </svg>
      </div>

      {/* Twinkles */}
      {[
        { x: "12%", y: "15%", d: "0s",   s: 1.2 },
        { x: "85%", y: "35%", d: "0.5s", s: 1.4 },
        { x: "22%", y: "75%", d: "1s",   s: 1.0 },
        { x: "75%", y: "85%", d: "1.5s", s: 1.2 },
        { x: "8%",  y: "55%", d: "2s",   s: 0.8 },
      ].map((p, i) => (
        <span key={i} className="absolute rounded-full ai-twinkle"
          style={{
            left: p.x, top: p.y,
            width: `${p.s * 6}px`, height: `${p.s * 6}px`,
            background: T.primary, boxShadow: `0 0 ${p.s * 8}px ${T.primaryGlow}`,
            animationDelay: p.d,
          }} />
      ))}
    </div>

    <style>{`
      @keyframes aiSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      .ai-spin-slow { animation: aiSpin 14s linear infinite; transform-origin: center; }
      @keyframes aiTwinkle { 0%,100% { opacity: 0.25; transform: scale(0.7); } 50% { opacity: 1; transform: scale(1.2); } }
      .ai-twinkle { animation: aiTwinkle 2.4s ease-in-out infinite; }
      @keyframes aiFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
      .ai-float { animation: aiFloat 4s ease-in-out infinite; }
      .ai-float-slow { animation: aiFloat 6s ease-in-out infinite; }
      @keyframes aiPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }
      .ai-pulse { animation: aiPulse 2.4s ease-in-out infinite; }
      @keyframes aiWave { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
      .ai-wave { transform-origin: bottom; animation: aiWave 0.8s ease-in-out infinite; }
    `}</style>
  </>
);

export function AuthBrand() {
  return (
    <a href="/design-preview/landing" className="inline-flex items-center gap-3">
      <div className="w-11 h-11 rounded-sm border-2 flex items-center justify-center font-bold text-xl"
        style={{ borderColor: T.primary, color: T.primary, boxShadow: `0 0 14px ${T.primaryGlow}`, fontFamily: "'Rajdhani', sans-serif" }}>
        E
      </div>
      <div>
        <div className="font-bold text-xl tracking-tight" style={{ color: T.text, fontFamily: "'Rajdhani', sans-serif" }}>
          EnglishTest<span style={{ color: T.primary }}>.UK</span>
        </div>
        <div className="mono text-[9px] tracking-[0.35em] mt-0.5" style={{ color: T.textMuted }}>// PASS · NOT JUST PRACTICE</div>
      </div>
    </a>
  );
}
